import { versionedAssetUrl } from '../assets/version';
import type { AudioManifest, ToneSpec } from './manifest';

/**
 * The SFX engine (spec.md §N task §7): event-name → sound, with master/sfx volume,
 * mute, random variant + pitch, per-key cooldown, autoplay safety, and graceful
 * fallback when audio is unavailable.
 *
 * All decision logic lives here and is pure/testable: the actual sound output goes
 * through an injected {@link AudioBackend} (WebAudio in the browser, a fake in tests),
 * the clock and RNG are injected, and persistence uses an injected storage shim. The
 * engine never throws on a missing key, a not-ready backend, or corrupt storage.
 */

export interface PlayRequest {
  key: string;
  /** Resolved file URL, or null to use the tone fallback. */
  file: string | null;
  tone: ToneSpec | null;
  /** Final gain 0..1 (entry × sfx × master). */
  volume: number;
  /** Playback rate multiplier. */
  pitch: number;
}

export interface AudioBackend {
  /** Whether audio may play yet (false until a user gesture unlocks autoplay). */
  ready(): boolean;
  play(req: PlayRequest): void;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface SfxEngineOptions {
  backend: AudioBackend;
  now?: () => number;
  random?: () => number;
  storage?: StorageLike;
  /** Prefix prepended to manifest file paths. */
  audioBase?: string;
  /** localStorage key for persisted settings. */
  storageKey?: string;
}

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

const DEFAULT_STORAGE_KEY = 'gravelight.audio';

export class SfxEngine {
  private readonly backend: AudioBackend;
  private readonly now: () => number;
  private readonly random: () => number;
  private readonly storage?: StorageLike;
  private readonly audioBase: string;
  private readonly storageKey: string;

  private masterVolume = 1;
  private sfxVolume = 1;
  private muted = false;
  private readonly lastPlayedAt = new Map<string, number>();

  constructor(
    private readonly manifest: AudioManifest,
    options: SfxEngineOptions,
  ) {
    this.backend = options.backend;
    this.now = options.now ?? (() => Date.now());
    this.random = options.random ?? Math.random;
    this.storage = options.storage;
    this.audioBase = options.audioBase ?? '/assets/audio/sfx/';
    this.storageKey = options.storageKey ?? DEFAULT_STORAGE_KEY;
    this.loadSettings();
  }

  /** Returns true if the sound was dispatched to the backend, false if it was skipped. */
  play(key: string, opts: { now?: number } = {}): boolean {
    if (this.muted) return false;
    const entry = this.manifest[key];
    if (!entry) return false; // unknown key — graceful no-op
    if (!this.backend.ready()) return false; // autoplay not unlocked yet

    const now = opts.now ?? this.now();
    const cooldown = entry.cooldownMs ?? 0;
    if (cooldown > 0) {
      const last = this.lastPlayedAt.get(key);
      if (last !== undefined && now - last < cooldown) return false;
    }

    const files = entry.files ?? [];
    const file = files.length > 0 ? this.resolveFile(this.pickVariant(files)) : null;
    const tone = entry.tone ?? null;
    const pitch = this.pickPitch(entry.randomPitch);
    const volume = clamp01((entry.volume ?? 1) * this.sfxVolume * this.masterVolume);

    this.backend.play({ key, file, tone, volume, pitch });
    this.lastPlayedAt.set(key, now);
    return true;
  }

  private pickVariant(files: string[]): string {
    if (files.length === 1) return files[0]!;
    const idx = Math.min(files.length - 1, Math.floor(this.random() * files.length));
    return files[idx]!;
  }

  private pickPitch(range?: [number, number]): number {
    if (!range) return 1;
    const [lo, hi] = range;
    return lo + this.random() * (hi - lo);
  }

  private resolveFile(file: string): string {
    // Site-relative runtime audio is replaced in place, so it gets the build
    // asset version; external/http(s) URLs pass through untouched.
    if (/^(https?:)?\//.test(file)) return versionedAssetUrl(file);
    return versionedAssetUrl(this.audioBase + file);
  }

  // ── volume / mute ───────────────────────────────────────────────────────────────
  setMasterVolume(v: number): void {
    this.masterVolume = clamp01(v);
    this.saveSettings();
  }
  getMasterVolume(): number {
    return this.masterVolume;
  }
  setSfxVolume(v: number): void {
    this.sfxVolume = clamp01(v);
    this.saveSettings();
  }
  getSfxVolume(): number {
    return this.sfxVolume;
  }
  setMuted(muted: boolean): void {
    this.muted = muted;
    this.saveSettings();
  }
  isMuted(): boolean {
    return this.muted;
  }
  toggleMute(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  // ── persistence ───────────────────────────────────────────────────────────────────
  private loadSettings(): void {
    if (!this.storage) return;
    try {
      const raw = this.storage.getItem(this.storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<{ master: number; sfx: number; muted: boolean }>;
      if (typeof parsed.master === 'number') this.masterVolume = clamp01(parsed.master);
      if (typeof parsed.sfx === 'number') this.sfxVolume = clamp01(parsed.sfx);
      if (typeof parsed.muted === 'boolean') this.muted = parsed.muted;
    } catch {
      /* corrupt value — keep defaults, never crash */
    }
  }

  private saveSettings(): void {
    if (!this.storage) return;
    try {
      this.storage.setItem(
        this.storageKey,
        JSON.stringify({ master: this.masterVolume, sfx: this.sfxVolume, muted: this.muted }),
      );
    } catch {
      /* storage unavailable — ignore */
    }
  }
}
